// src/views/operateur/OperateurMemoire.jsx — LOT 2 OPÉRATEUR (pièce D,
// ADR-304) : la bascule « Retenu de vous » — ce qu'il a retenu de Dimitri.
//
// Textes du proto AU MOT PRÈS (docs/handoff/admin-proto.html, opMemoire) :
// chapeau, ligne « règle + portée · source », tag « nouvelle » (Garder / Non),
// « Oublier », note verte « votre fiche le dit aussi — nous sommes d'accord »,
// micro « Lui apprendre une règle » HONNÊTE pas-encore. « Tout effacer »
// (tout_oublier) passe par une confirmation À L'ÉCRAN avant l'appel.
//
// Tout vient du serveur (operateur_memoire_list / operateur_memoire_decide) —
// l'écran ne fabrique JAMAIS une règle, et une table absente se DIT.

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../supabase'

async function postAction(body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) { const e = new Error(json?.message || json?.error || `Erreur ${res.status}`); e.code = json?.error; throw e }
  return json
}

const microIc = (t) => (
  <svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" /><path d="M12 18v4" />
  </svg>
)

export default function OperateurMemoire({ nomLogement }) {
  const [source, setSource] = useState({ ok: null, regles: [], actif: true })
  const [microNote, setMicroNote] = useState(false)
  const [confirmeTout, setConfirmeTout] = useState(false)
  const [erreur, setErreur] = useState(null)
  const vivantRef = useRef(true)
  useEffect(() => { vivantRef.current = true; return () => { vivantRef.current = false } }, [])

  const charger = useCallback(async () => {
    try {
      const json = await postAction({ action: 'operateur_memoire_list' })
      if (!vivantRef.current) return
      setSource({ ok: true, regles: json.regles || [], actif: json.actif !== false })
    } catch (e) {
      console.error('[operateur] memoire', e?.message || e)
      if (vivantRef.current) setSource({ ok: false, regles: [], actif: true })
    }
  }, [])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])

  const decider = useCallback(async (geste, id) => {
    setErreur(null)
    try {
      await postAction({ action: 'operateur_memoire_decide', geste, id: id || null })
      charger()
    } catch (e) {
      setErreur(e?.message || 'La décision a échoué.')
    }
  }, [charger])

  const porteeDe = (r) => {
    if (r.portee_type === 'partout') return 'partout'
    if (r.portee_type === 'logement') return nomLogement ? nomLogement(r.portee_id) : r.portee_id
    return r.portee_id
  }

  if (source.ok === null) return <p className="note" data-testid="memoire-lecture">On lit ce qu'il a retenu…</p>
  if (source.ok === false) return <p className="note" data-testid="memoire-echec">La lecture de sa mémoire a échoué.</p>
  if (!source.actif) {
    return (
      <p className="note" data-testid="memoire-inerte">
        Sa mémoire n'est pas encore en service : sa mise en base n'est pas appliquée.
      </p>
    )
  }

  const regles = source.regles

  return (
    <div data-testid="operateur-memoire">
      {/* Le chapeau — au mot près du proto. */}
      <p className="note" style={{ margin: '0 0 16px' }}>
        Il lit vos contrats, vos fiches et vos messages tout seul. Ici, seulement
        ce qu'aucun document ne dit — vos habitudes, déduites de vos corrections.
      </p>

      {erreur && (
        <div data-testid="memoire-erreur" style={{ margin: '0 0 10px', padding: '9px 12px',
          background: 'var(--terra-bg)', border: '1px solid var(--terra-bd)', borderRadius: 12,
          fontSize: 11, color: 'var(--terra-tx)' }}>{erreur}</div>
      )}

      {regles.length === 0 && (
        <p className="note" data-testid="memoire-vide">Il n'a encore rien retenu de vous.</p>
      )}

      {regles.map((r) => {
        const propose = r.statut === 'proposee'
        return (
          <div key={r.id} data-testid={`memoire-regle-${r.id}`}
            style={{ display: 'flex', gap: 12, padding: '14px 0', borderTop: '1px solid var(--bord)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 6,
              background: propose ? '#87b8e6' : '#c3cdd6' }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, lineHeight: 1.45, color: 'var(--navy-d)' }}>
                {r.texte}
              </span>
              <span style={{ display: 'block', fontSize: 10.5, color: 'var(--gris-d)', marginTop: 5 }}>
                {porteeDe(r)} · {r.source || '—'}
              </span>
              {r.meta && r.meta.fiche_accord && (
                <span data-testid={`memoire-regle-${r.id}-note`}
                  style={{ display: 'block', fontSize: 10.5, color: 'var(--vert-tx)', marginTop: 5 }}>
                  votre fiche le dit aussi — nous sommes d'accord
                </span>
              )}
              {propose && (
                <span style={{ display: 'flex', gap: 14, marginTop: 9 }}>
                  <button type="button" data-testid={`memoire-regle-${r.id}-garder`}
                    onClick={() => decider('garder', r.id)}
                    style={{ fontSize: 11, fontWeight: 600, color: 'var(--vert-tx)', background: 'none',
                      border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>Garder</button>
                  <button type="button" data-testid={`memoire-regle-${r.id}-non`}
                    onClick={() => decider('oublier', r.id)}
                    style={{ fontSize: 11, color: 'var(--gris)', background: 'none', border: 'none',
                      padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>Non</button>
                </span>
              )}
            </span>
            {propose ? (
              <span className="tag t-b" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>nouvelle</span>
            ) : (
              <button type="button" data-testid={`memoire-regle-${r.id}-oublier`}
                onClick={() => decider('oublier', r.id)}
                style={{ fontSize: 10.5, color: 'var(--gris-d)', flexShrink: 0, alignSelf: 'flex-start',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                Oublier
              </button>
            )}
          </div>
        )
      })}

      <div style={{ height: 6 }} />
      {/* Le micro — bouton HONNÊTE « pas encore » (même règle que le lot 1). */}
      <button type="button" data-testid="memoire-micro" onClick={() => setMicroNote((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          background: '#fff', border: '1px dashed #c3d5e4', borderRadius: 12, padding: 12,
          minHeight: 50, marginTop: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
        <span style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--ciel-p)',
          color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0 }}>{microIc(12)}</span>
        <span style={{ flex: 1, fontSize: 11.5, color: 'var(--navy-d)' }}>Lui apprendre une règle</span>
      </button>
      {microNote && (
        <p className="note" data-testid="memoire-micro-note" style={{ marginTop: 8 }}>
          Pas encore — lui parler arrivera avec l'opérateur complet.
        </p>
      )}

      {/* Tout effacer — confirmation À L'ÉCRAN avant tout_oublier. */}
      {regles.length > 0 && !confirmeTout && (
        <button type="button" data-testid="memoire-tout-effacer" onClick={() => setConfirmeTout(true)}
          style={{ display: 'block', marginTop: 14, fontSize: 10.5, color: 'var(--gris-d)',
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
          Tout effacer
        </button>
      )}
      {confirmeTout && (
        <div data-testid="memoire-tout-effacer-confirme" style={{ marginTop: 12, padding: '11px 12px',
          background: 'var(--terra-bg)', border: '1px solid var(--terra-bd)', borderRadius: 12 }}>
          <p style={{ margin: '0 0 9px', fontSize: 11.5, color: 'var(--terra-tx)', lineHeight: 1.5 }}>
            Tout effacer ? Il ne retiendra plus rien de vous — chaque règle devra être réapprise.
          </p>
          <div className="act" style={{ margin: 0 }}>
            <button type="button" className="bt bt-p" data-testid="memoire-tout-effacer-oui"
              style={{ background: 'var(--terra)' }}
              onClick={async () => { setConfirmeTout(false); await decider('tout_oublier', null) }}>
              Effacer tout
            </button>
            <button type="button" className="bt bt-s" onClick={() => setConfirmeTout(false)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  )
}
